import React, { useEffect, useState } from "react";
import axios from "axios";
import { LineChart } from "@mui/x-charts/LineChart";
import apiUrl from "../../Apiurl";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import { LineChartSkeleton } from "../../interfaccia/loading";
import SettingBooleano from "../../statistiche/SettingBooleano";

const SERIES_PALETTE = [
  "#ff9800",
  "#009688",
  "#1565c0",
  "#9c27b0",
  "#b71c1c",
  "#e91e63",
  "#8bc34a",
  "#00bcd4",
  "#795548",
  "#607d8b",
  "#ffeb3b",
  "#3f51b5",
  "#f44336",
  "#9e9e9e",
  "#4caf50",
  "#ffc107",
  "#03a9f4",
  "#673ab7",
  "#cddc39",
];

function StatTrendSharing({ mobile, token }) {
  const {
    buttonClassesWhite,
    buttonClassesGreen,
    buttonClassesOrange,
    inputClassesRicercaOrizzontale,
  } = stilePulsanti;

  const [years, setYears] = useState(3);
  const [metric, setMetric] = useState("importo");
  const [scope, setScope] = useState("all");
  const [viewMode, setViewMode] = useState("comparison");
  const [chartData, setChartData] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [visibleSeries, setVisibleSeries] = useState({});
  const [visibleComparisonSeries, setVisibleComparisonSeries] = useState({
    sharing: true,
    totale: true,
    percentuale: true,
  });
  const [seriesColors, setSeriesColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return undefined;

    const controller = new AbortController();
    let active = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          anni: String(years),
          metric: metric === "importo" ? "importo" : "count",
          tipo: scope,
        });
        const response = await axios.get(
          `${apiUrl}/logistica/dashboard/statisticheArticoliSharingTrend?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );

        if (!active) return;

        const incomingSeries = Array.isArray(response.data?.series)
          ? response.data.series
          : [];
        const incomingDataset = Array.isArray(response.data?.dataset)
          ? response.data.dataset
          : [];
        const incomingComparisonDataset = Array.isArray(
          response.data?.comparisonDataset
        )
          ? response.data.comparisonDataset
          : [];
        const processedDataset = incomingDataset
          .map((row) => {
            if (!row?.month) return null;

            const month = new Date(`${row.month}-01T00:00:00`);
            if (Number.isNaN(month.getTime())) return null;

            const processedRow = { month };
            incomingSeries.forEach(({ key }) => {
              const value = Number(row[key]);
              processedRow[key] = Number.isFinite(value) ? value : 0;
            });
            return processedRow;
          })
          .filter(Boolean)
          .sort((rowA, rowB) => rowA.month - rowB.month);
        const processedComparisonDataset = incomingComparisonDataset
          .map((row) => {
            if (!row?.month) return null;

            const month = new Date(`${row.month}-01T00:00:00`);
            if (Number.isNaN(month.getTime())) return null;

            return {
              month,
              sharing: Number(row.sharing) || 0,
              totale: Number(row.totale) || 0,
              percentuale: Number(row.percentuale) || 0,
            };
          })
          .filter(Boolean)
          .sort((rowA, rowB) => rowA.month - rowB.month);

        const colors = {};
        const visibility = {};
        incomingSeries.forEach(({ key }, index) => {
          colors[key] = SERIES_PALETTE[index % SERIES_PALETTE.length];
          visibility[key] = index < 5;
        });

        setChartData(processedDataset);
        setComparisonData(processedComparisonDataset);
        setSeriesList(incomingSeries);
        setSeriesColors(colors);
        setVisibleSeries(visibility);
      } catch (err) {
        if (!active || axios.isCancel(err) || err?.code === "ERR_CANCELED") return;
        setError("Errore caricamento dati");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();

    return () => {
      active = false;
      controller.abort();
    };
  }, [scope, years, metric, token]);

  const sharingLabel =
    scope === "inv_sharing"
      ? "Inventory Sharing"
      : scope === "dropshipping"
      ? "Dropshipping"
      : "Condiviso totale";

  const renderViewSelector = () => (
    <div className="flex flex-wrap items-center justify-center mb3">
      <span className="mr2">Vista:</span>
      <button
        onClick={() => setViewMode("comparison")}
        className={viewMode === "comparison" ? buttonClassesGreen : buttonClassesWhite}
      >
        Sharing sul totale
      </button>
      <button
        onClick={() => setViewMode("brands")}
        className={viewMode === "brands" ? buttonClassesGreen : buttonClassesWhite}
      >
        Sharing per brand
      </button>
    </div>
  );

  const renderControls = () => (
    <div className="flex flex-wrap items-center justify-between mb2">
      <div className="flex items-center mb2 mr3">
        <span className="mr2">Scope:</span>
        <button
          onClick={() => setScope("all")}
          className={scope === "all" ? buttonClassesGreen : buttonClassesWhite}
        >
          Tutti
        </button>
        <button
          onClick={() => setScope("inv_sharing")}
          className={scope === "inv_sharing" ? buttonClassesGreen : buttonClassesWhite}
        >
          Inventory Sharing
        </button>
        <button
          onClick={() => setScope("dropshipping")}
          className={scope === "dropshipping" ? buttonClassesGreen : buttonClassesWhite}
        >
          Dropshipping
        </button>
      </div>

      <div className="flex items-center mb2">
        <div className="mr3 flex items-center">
          <span className="mr2">ultimi</span>
          <input
            type="number"
            min={1}
            max={5}
            value={years}
            onChange={(event) => {
              const value = Number(event.target.value);
              setYears(Math.min(5, Math.max(1, value)));
            }}
            className={`${inputClassesRicercaOrizzontale} w3 tc`}
          />
          <span className="ml2">anni</span>
        </div>

        <div className="mr3 flex items-center">
          <SettingBooleano
            value={metric}
            trueButtonClass={buttonClassesOrange}
            falseButtonClass={buttonClassesGreen}
            trueElement="importo"
            falseElement="quantità"
            setTrue={() => setMetric("importo")}
            setFalse={() => setMetric("quantita")}
            customValueTrue="importo"
            customValueFalse="quantita"
          />
        </div>
      </div>
    </div>
  );

  const renderToggles = () => {
    if (viewMode === "comparison") {
      const comparisonSeries = [
        { key: "sharing", label: sharingLabel, color: "#ff9800" },
        { key: "totale", label: "Totale vendite", color: "#1565c0" },
        { key: "percentuale", label: "Incidenza sharing (%)", color: "#6a1b9a" },
      ];

      return (
        <div className="flex flex-wrap mb2 justify-center">
          {comparisonSeries.map(({ key, label, color }) => {
            const isActive = visibleComparisonSeries[key];

            return (
              <button
                key={key}
                onClick={() =>
                  setVisibleComparisonSeries((previous) => ({
                    ...previous,
                    [key]: !previous[key],
                  }))
                }
                className="ma1 pa2 pointer br2 b--none white f7"
                style={{
                  backgroundColor: isActive ? color : "#eee",
                  color: isActive ? "#fff" : "#333",
                  border: isActive ? "none" : "1px solid #ccc",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap mb2 justify-center">
        {seriesList.map(({ key, label }) => {
          const isActive = visibleSeries[key];
          const color = seriesColors[key] || "#000";

          return (
            <button
              key={key}
              onClick={() =>
                setVisibleSeries((previous) => ({
                  ...previous,
                  [key]: !previous[key],
                }))
              }
              className="ma1 pa2 pointer br2 b--none white f7"
              style={{
                backgroundColor: isActive ? color : "#eee",
                color: isActive ? "#fff" : "#333",
                border: isActive ? "none" : "1px solid #ccc",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderChart = () => {
    if (loading) {
      return (
        <LineChartSkeleton
          tone="soft"
          height={mobile ? 300 : 700}
          seriesCount={
            viewMode === "comparison"
              ? 3
              : Math.max(2, Math.min(seriesList.length || 4, 6))
          }
        />
      );
    }
    if (error) return <div className="dark-red tc pa3">{error}</div>;
    if (viewMode === "comparison" && comparisonData.length === 0) {
      return <div className="gray tc pa3">Nessun dato presente nel periodo</div>;
    }
    if (viewMode === "brands" && (chartData.length === 0 || seriesList.length === 0)) {
      return <div className="gray tc pa3">Nessun brand presente nel periodo</div>;
    }

    const comparisonSeries = [
      {
        key: "sharing",
        label: sharingLabel,
        color: "#ff9800",
        yAxisKey: "values",
      },
      {
        key: "totale",
        label: "Totale vendite",
        color: "#1565c0",
        yAxisKey: "values",
      },
      {
        key: "percentuale",
        label: "Incidenza sharing (%)",
        color: "#6a1b9a",
        yAxisKey: "percentage",
      },
    ];
    const activeSeries =
      viewMode === "comparison"
        ? comparisonSeries.filter(({ key }) => visibleComparisonSeries[key])
        : seriesList
            .filter(({ key }) => visibleSeries[key])
            .map((item) => ({
              ...item,
              color: seriesColors[item.key],
              yAxisKey: "values",
            }));
    if (activeSeries.length === 0) {
      return <div className="gray tc pa3">Seleziona almeno una serie</div>;
    }

    const valueFormatter = (value) => {
      if (metric === "importo") {
        return `${Number(value).toLocaleString("it-IT", {
          minimumFractionDigits: 2,
        })} €`;
      }
      return value;
    };
    const percentageFormatter = (value) =>
      `${Number(value).toLocaleString("it-IT", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;
    const dataset = viewMode === "comparison" ? comparisonData : chartData;
    const series = activeSeries.map(({ key, label, color, yAxisKey }) => ({
      dataKey: key,
      label,
      valueFormatter: key === "percentuale" ? percentageFormatter : valueFormatter,
      showMark: true,
      strokeWidth: 2,
      color,
      yAxisKey,
      data: dataset.map((row) => row[key]),
    }));

    return (
      <div style={{ width: "100%", height: mobile ? 300 : 700 }}>
        <LineChart
          dataset={dataset}
          xAxis={[
            {
              dataKey: "month",
              scaleType: "time",
              valueFormatter: (value) =>
                value
                  ? new Date(value).toLocaleDateString("it-IT", {
                      month: "2-digit",
                      year: "2-digit",
                    })
                  : "",
            },
          ]}
          yAxis={[
            { id: "values", position: "left" },
            ...(viewMode === "comparison"
              ? [
                  {
                    id: "percentage",
                    position: "right",
                    min: 0,
                    max: 100,
                    valueFormatter: percentageFormatter,
                  },
                ]
              : []),
          ]}
          series={series}
          height={mobile ? 300 : 700}
          margin={{
            left: 70,
            right: viewMode === "comparison" ? 70 : 40,
            top: 20,
            bottom: 50,
          }}
        />
      </div>
    );
  };

  return (
    <div className="sfondoBiancoTema pa3 mb3">
      <div className="tc b mb2 f4">
        {viewMode === "comparison"
          ? `Trend Condiviso sul totale (${metric})`
          : `Trend Condiviso per brand (${metric})`}
      </div>
      {renderViewSelector()}
      {renderControls()}
      {renderToggles()}
      {renderChart()}
    </div>
  );
}

export default StatTrendSharing;
